/**
 * MeetingSubHeader — sub-header content for an ahoy_meeting slideout.
 *
 * Layout differs from the other sub-header types: a flex row with the start
 * time + duration on the left and an optional "Join meeting" button on the
 * right. Doesn't use SubHeaderLayout because the join-button affordance
 * doesn't fit the primary/secondary/tertiary model.
 *
 * Registered on `ENTITY_TYPE_REGISTRY.ahoy_meeting.components.subHeader`.
 */

import { VideoRecorder } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useRefreshAt } from '@/hooks/useRefreshAt';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Badge } from '@/lib/base/badges/badges';
import { useOrgTimeFormat } from '@/lib/time';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { EmailThreadVisibilityBadge } from '../../components/EmailThreadVisibilityBadge';
import { NotOnCalendarBadge } from '../../components/NotOnCalendarBadge';
import { isMeetingRecurring } from '../hooks/useMeetingSeries';
import { useMeetingVisibilityActions } from '../hooks/useMeetingVisibilityActions';
import {
  formatMeetingDate,
  formatMeetingTime,
  getMeetingDurationMinutes,
  getVideoPlatform,
  isMeetingAdHoc,
  isMeetingToday,
  looksLikeUrl,
  platformIcons,
  platformNames,
} from '../utils/meetingHelpers';

export function MeetingSubHeader({ object }: { object: CrmObject }) {
  const { data: profileData } = useUserProfileData();
  const userTimeZone = profileData?.user?.timezone || undefined;
  const { timeFormat } = useOrgTimeFormat();

  const startTime = getPropertyValue<string>(
    object.properties?.ahoy_start_time
  );
  const endTime = getPropertyValue<string>(object.properties?.ahoy_end_time);
  const meetLink = getPropertyValue<string>(
    object.properties?.ahoy_meeting_link
  )?.trim();
  const location = getPropertyValue<string>(
    object.properties?.ahoy_location
  )?.trim();
  const locationAsMeetLink =
    location && looksLikeUrl(location) ? location : undefined;
  const rawMeetLink = meetLink || locationAsMeetLink || undefined;
  // Normalize before platform detection / opening: scheme-less values
  // (`meet.google.com/xyz`, `zoom.us/j/...`) wouldn't satisfy
  // `getVideoPlatform`'s `new URL()` validity check or `new URL()` in
  // `handleJoin`, suppressing the Join button entirely. Prefixing
  // `https://` lets both code paths work uniformly.
  let effectiveMeetLink: string | undefined;
  if (rawMeetLink) {
    effectiveMeetLink = /^https?:\/\//i.test(rawMeetLink)
      ? rawMeetLink
      : `https://${rawMeetLink}`;
  }

  const formattedStartTime = startTime
    ? formatMeetingTime(startTime, userTimeZone, timeFormat)
    : '';
  const formattedEndTime = endTime
    ? formatMeetingTime(endTime, userTimeZone, timeFormat)
    : '';
  // Show the date only when the meeting isn't today — today's meeting reads
  // fine as just a time, but any other day needs the date for context.
  const formattedDate =
    startTime && !isMeetingToday(startTime, userTimeZone)
      ? formatMeetingDate(startTime, userTimeZone)
      : '';
  const durationMinutes =
    startTime && endTime ? getMeetingDurationMinutes(startTime, endTime) : null;
  const videoPlatform = getVideoPlatform(effectiveMeetLink);
  // Show Join whenever a video link exists and the meeting is not definitely
  // over. "Definitely over" means the end time is known (finite) and has
  // passed — strict `end > now`, the same boundary the dashboard row uses. A
  // meeting with no/unparseable end time can't be known to be over, so it
  // keeps its Join affordance. There is no start-side gate: Join renders well
  // before the meeting begins.
  const endTimeMs = endTime ? new Date(endTime).getTime() : NaN;
  const hasEnded = Number.isFinite(endTimeMs) && endTimeMs <= Date.now();
  // Re-render at the end boundary so Join disappears without a manual refresh.
  // useRefreshAt ignores past/non-finite targets.
  useRefreshAt(Number.isFinite(endTimeMs) ? endTimeMs : null);
  const isJoinable = !!(videoPlatform && effectiveMeetLink && !hasEnded);

  // Timezone abbreviation label (e.g. "PST", "EST", "GMT+1")
  const timezoneLabel = useMemo(() => {
    if (!startTime) {
      return '';
    }
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZoneName: 'short',
        ...(userTimeZone ? { timeZone: userTimeZone } : {}),
      }).formatToParts(new Date(startTime));
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      return tzPart ? `(${tzPart.value})` : '';
    } catch {
      return '';
    }
  }, [startTime, userTimeZone]);

  const handleJoin = useCallback(() => {
    if (effectiveMeetLink) {
      try {
        const url = new URL(effectiveMeetLink);
        if (url.protocol === 'https:' || url.protocol === 'http:') {
          window.open(effectiveMeetLink, '_blank', 'noopener,noreferrer');
        }
      } catch {
        // Invalid URL — do nothing
      }
    }
  }, [effectiveMeetLink]);

  const title =
    getPropertyValue<string>(object.properties?.ahoy_title) || 'meeting';

  const recurring = isMeetingRecurring(object);
  const recurringBadge = recurring ? (
    <Badge type='pill-color' color='gray' size='sm'>
      Recurring
    </Badge>
  ) : null;

  const adHoc = isMeetingAdHoc(object);

  const visibilityActions = useMeetingVisibilityActions(object);

  if (!startTime) {
    // With no start time, the subheader carries only the recurrence badge,
    // the ad-hoc badge, and/or the visibility badge. When none are present,
    // render nothing rather than an empty wrapper that adds blank spacing
    // below the title.
    if (!recurringBadge && !object.access_summary && !adHoc) {
      return null;
    }
    return (
      <div className='flex items-center gap-2'>
        {recurringBadge}
        {adHoc && <NotOnCalendarBadge />}
        <EmailThreadVisibilityBadge
          access_summary={object.access_summary}
          visibilityActions={visibilityActions}
        />
      </div>
    );
  }

  return (
    <div className='flex items-center justify-between gap-2'>
      <div className='flex items-center gap-1.5 text-xs md:text-sm text-fg-secondary'>
        {formattedDate && (
          <>
            <span className='font-medium text-fg-primary'>{formattedDate}</span>
            <span aria-hidden='true'>·</span>
          </>
        )}
        <span>
          {formattedStartTime}
          {formattedEndTime && `–${formattedEndTime}`}
        </span>
        {timezoneLabel && <span>{timezoneLabel}</span>}
        {durationMinutes !== null && (
          <>
            <span aria-hidden='true'>·</span>
            <span>
              {durationMinutes} min{durationMinutes !== 1 ? 's' : ''}
            </span>
          </>
        )}
      </div>
      <div className='flex items-center gap-2'>
        {recurringBadge}
        {adHoc && <NotOnCalendarBadge />}
        <EmailThreadVisibilityBadge
          access_summary={object.access_summary}
          visibilityActions={visibilityActions}
        />
        {isJoinable && (
          <button
            type='button'
            onClick={handleJoin}
            className='flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-fg-secondary border border-[var(--color-border-secondary)] rounded-lg shadow-xs hover:bg-[var(--color-bg-tertiary)] transition-colors cursor-pointer'
            aria-label={`Join ${title} on ${videoPlatform ? platformNames[videoPlatform] : 'video call'}`}
          >
            {videoPlatform && platformIcons[videoPlatform] ? (
              <img
                src={platformIcons[videoPlatform]}
                alt=''
                className='size-4'
              />
            ) : (
              <VideoRecorder className='size-4' aria-hidden='true' />
            )}
            Join meeting
          </button>
        )}
      </div>
    </div>
  );
}
